import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

import { UserDetailsDto } from '../../../common/dtos/user-details.dto';
import {
    GranteeIneligibleReasons,
    IneligibleReasons,
} from '../../../common/enums/ineligible-scenarios.enum';

@Injectable({
    providedIn: 'root',
})
export class IneligibleScenariosService {
    private readonly httpClient = inject(HttpClient);


    /**
     * Retrieves ineligible scenarios for the grantor (person granting access).
     * 
     * @returns Observable<Array<IneligibleReasons>> - List of reasons why the grantor cannot grant access
     * 
     * @remarks
     * This API checks if the grantor has any conditions that prevent them from granting
     * access to their accounts (e.g., certain account types, legal restrictions).
     */
    getIneligibleScenariosForGrantor(): Observable<Array<IneligibleReasons>> {
        return this.httpClient.get<Array<IneligibleReasons>>(
            `${environment.ZCI_API_BASE_URL}${environment.ZCI_API_ENDPOINTS.INELIGIBLE_SCENARIOS_GRANTOR}`,
        );
    }

    /**
     * Checks if a specific grantee is eligible to receive access based on their details.
     * 
     * @param userDetails - The user information to check for eligibility
     * @returns Observable<{ granteeIneligibleReason: GranteeIneligibleReasons }> - The reason for ineligibility, if any
     * 
     * @remarks
     * This API is used during the Grant Access Flow (GAF) to validate that a potential
     * grantee meets all requirements to receive account access.
     */
    getIneligibleScenariosForGrantee(
        userDetails: UserDetailsDto,
    ): Observable<{ granteeIneligibleReason: GranteeIneligibleReasons }> {
        return this.httpClient.post<{ granteeIneligibleReason: GranteeIneligibleReasons }>(
            `${environment.ZCI_API_BASE_URL}${environment.ZCI_API_ENDPOINTS.INELIGIBLE_SCENARIOS_GRANTEE}`,
            userDetails,
        );
    }

    /**
     * Checks grantee eligibility in the context of the grantee flow (when grantee is accepting access).
     * 
     * @returns Observable<{ granteeIneligibleReason: GranteeIneligibleReasons }> - The reason for ineligibility, if any
     * 
     * @remarks
     * This API is called during the grantee acceptance flow to verify that the grantee
     * can complete the process. It uses the authenticated grantee's session.
     */
    getIneligibleScenariosForGranteeInGranteeFlow(): Observable<{
        granteeIneligibleReason: GranteeIneligibleReasons;
    }> {
        return this.httpClient.get<{ granteeIneligibleReason: GranteeIneligibleReasons }>(
            `${environment.ZCI_API_BASE_URL}${environment.ZCI_API_ENDPOINTS.INELIGIBLE_SCENARIOS_GRANTEE_IN_GRANTEE_FLOW}`,
        );
    }
}
